import type { QueryClient } from '@tanstack/react-query';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { db } from '@/lib/db/index';
import type { Mold } from '@/types/maintenance';

// ─── Work orders ──────────────────────────────────────────────────────────────

export function subscribeWorkOrders(
  orgId: string,
  queryClient: QueryClient,
): RealtimeChannel {
  return supabase
    .channel(`work_orders:${orgId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'work_orders',
        filter: `organization_id=eq.${orgId}`,
      },
      (payload) => {
        // Realtime payloads don't include joined relations — invalidate React Query
        // which will re-fetch with full joins. IndexedDB update happens via write-through
        // in the next fetchWorkOrders call.
        if (payload.eventType === 'DELETE') {
          db.work_orders.delete(payload.old.id as string).catch(() => {});
        }
        queryClient.invalidateQueries({ queryKey: ['work_orders'] });
      },
    )
    .subscribe();
}

// ─── Molds ────────────────────────────────────────────────────────────────────

export function subscribeMolds(
  orgId: string,
  queryClient: QueryClient,
): RealtimeChannel {
  return supabase
    .channel(`molds:${orgId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'molds',
        filter: `organization_id=eq.${orgId}`,
      },
      (payload) => {
        if (payload.eventType === 'DELETE') {
          db.molds.delete(payload.old.id as string).catch(() => {});
        } else {
          db.molds.put(payload.new as unknown as Mold).catch(() => {});
        }
        queryClient.invalidateQueries({ queryKey: ['molds'] });
      },
    )
    .subscribe();
}

// ─── Spare parts ──────────────────────────────────────────────────────────────

export function subscribeSpareParts(
  orgId: string,
  queryClient: QueryClient,
): RealtimeChannel {
  return supabase
    .channel(`spare_parts:${orgId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'spare_parts',
        filter: `organization_id=eq.${orgId}`,
      },
      () => {
        queryClient.invalidateQueries({ queryKey: ['spare_parts'] });
      },
    )
    .subscribe();
}

// ─── Failure codes ────────────────────────────────────────────────────────────

export function subscribeFailureCodes(
  orgId: string,
  queryClient: QueryClient,
): RealtimeChannel {
  return supabase
    .channel(`failure_codes:${orgId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'failure_codes',
        filter: `organization_id=eq.${orgId}`,
      },
      () => {
        queryClient.invalidateQueries({ queryKey: ['failure_codes'] });
      },
    )
    .subscribe();
}

// ─── Action codes ─────────────────────────────────────────────────────────────

export function subscribeActionCodes(
  orgId: string,
  queryClient: QueryClient,
): RealtimeChannel {
  return supabase
    .channel(`action_codes:${orgId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'action_codes',
        filter: `organization_id=eq.${orgId}`,
      },
      () => {
        queryClient.invalidateQueries({ queryKey: ['action_codes'] });
      },
    )
    .subscribe();
}

// ─── Notifications ───────────────────────────────────────────────────────────

const CRITICAL_NOTIFICATION_TYPES = new Set(['line_down', 'fix_validation_failed', 'blocked_escalation']);

export function subscribeNotifications(
  userId: string,
  queryClient: QueryClient,
  onCritical?: (type: string, title: string) => void,
): RealtimeChannel {
  return supabase
    .channel(`notifications:${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
        const row = payload.new as { type?: string; title?: string };
        if (row.type && CRITICAL_NOTIFICATION_TYPES.has(row.type) && onCritical) {
          onCritical(row.type, row.title ?? '');
        }
      },
    )
    .subscribe();
}

// ─── Unsubscribe ──────────────────────────────────────────────────────────────

export function unsubscribeChannel(channel: RealtimeChannel): void {
  supabase.removeChannel(channel).catch(() => {});
}
